import * as turf from "@turf/turf";
import type { Feature, Polygon, Position } from "geojson";

/** Una pieza padre del paso 3, tal como la conoce el cliente. */
export interface AoiPieza {
  /** Posición **1-based** en el arreglo de piezas.
   *
   *  Es el MISMO número que la columna `indice` de `vw_poligonos_padre`, que
   *  sale de `(d.path)[1]` del `ST_Dump` sobre `poligonos_padre`
   *  (`sql/001_vista_poligonos_padre.sql`). Coinciden porque `guardarAoiAsync`
   *  manda el arreglo COMPLETO de piezas en cada guardado, en este orden: la
   *  posición N de acá es el elemento N del `MultiPolygon` de la base.
   *
   *  **Vale mientras no haya un guardado en vuelo.** Entre que se agrega o se
   *  quita una pieza y que el guardado vuelve, la base todavía tiene la
   *  numeración anterior. Todo lo que usa el `indice` contra el servidor —el
   *  resalte de la pieza y su lectura en la edición— corre con el usuario
   *  quieto, después del guardado, así que en la práctica no se cruzan. */
  indice: number;
  polygon: Polygon;
  /** Id de la fila de esta pieza en `tbl_temp_poligonos_padre`, o `null` si
   *  todavía no está guardada (recién dibujada, importada de un KML, o
   *  reconstruida de los lotes).
   *
   *  **Es lo que el `indice` no puede ser.** El índice es una POSICIÓN: se
   *  corre en cuanto el usuario borra otra pieza, así que no se le puede colgar
   *  nada que viva más que un guardado. Con este id el modo Editar le pide al
   *  servidor los vértices de ESTA pieza y sigue pidiendo los mismos aunque
   *  cambie de lugar en el arreglo.
   *
   *  Lo asigna el servidor y lo adopta `adoptarIds` con la respuesta del
   *  guardado. Una pieza sin id no se puede editar por vértices todavía: no
   *  tiene vértices extraídos. */
  id: string | null;
  /** `false` solo para una pieza restaurada que el usuario no tocó. Es lo que
   *  el servidor mira para decidir si conserva los lotes en que ya se
   *  subdividió (ver `ConfirmarPiezas`). */
  modificada: boolean;
}

/** Entrada interna: la pieza más lo que cuesta recalcular en cada consulta. */
interface Entrada {
  polygon: Polygon;
  modificada: boolean;
  /** Ver `AoiPieza.id`. */
  id: string | null;
  /** La misma geometría como `Feature` de turf, armada UNA vez.
   *
   *  No es un lujo: `_segmentoAtraviesa` y `_piezaQueContiene` corren en cada
   *  `mousemove` mientras se dibuja, contra todas las piezas. Antes cada una
   *  de esas vueltas llamaba a `layerToTurfPolygon(capa)`, que hace un
   *  `toGeoJSON()` del polígono entero — con un AOI importado de miles de
   *  vértices, por cuadro. */
  feature: Feature<Polygon> | null;
  /** `[minLng, minLat, maxLng, maxLat]`, para descartar por caja antes de
   *  tocar turf. */
  bbox: [number, number, number, number] | null;
}

/** Las piezas padre del paso 3, **fuera de Leaflet**.
 *
 *  **Por qué existe este archivo.** Hasta acá las piezas eran capas de Leaflet
 *  (`L.FeatureGroup`) y esas capas cumplían DOS papeles a la vez: eran el
 *  modelo de datos del paso —de ahí salían el área del badge, las piezas del
 *  `ConfirmarPiezas`, la unión topológica y la validación de solapes— y además
 *  se pintaban. Pintarse era el problema: la capa WMS de GeoServer
 *  (`vw_poligonos_padre`) ya dibuja las mismas piezas, con un SLD que es el
 *  port exacto de `POLYGON_STYLE`, así que cada pieza guardada se renderizaba
 *  dos veces, una encima de la otra. No se notaba porque los dos estilos son
 *  idénticos; lo único que lo delataba era la opacidad (dos rellenos al 25%
 *  apilados dan ~44%).
 *
 *  Sacarlas del render obligaba a sacarlas del modelo, y eso es esto: GeoJSON
 *  plano más turf, sin una sola capa de Leaflet. El trabajo real siempre fue
 *  de turf — las capas se convertían a turf en cada consulta—, así que acá no
 *  se perdió nada y se dejó de pagar la conversión.
 *
 *  **Quién pinta, entonces.** Una pieza guardada la pinta SOLO el WMS. Leaflet
 *  dibuja el trazo mientras se traza, la pieza recién cerrada hasta que el WMS
 *  la incluye, y nada más.
 *
 *  **El hit-testing es local.** Sin capas no hay `layer.on("click")`, así que
 *  el click del mapa se resuelve con `turf.booleanPointInPolygon` contra este
 *  modelo (`enElPunto`). Es lo que hacía la app de referencia antes de que se
 *  le pusieran capas propias, y para unas pocas piezas no se mide.
 */
export class AoiPiezasState {
  /** Pisa todas las piezas. Lo usan la restauración del borrador y el KML del
   *  paso 2, que llegan sin modificar (`modificada: false`). */
  reemplazar(
    polygons: readonly Polygon[],
    modificada = false,
    /** Los ids que el servidor ya tiene para esas piezas, en la misma posición.
     *  Más corto (o con huecos) cuando el registro es anterior a
     *  `tbl_temp_poligonos_padre`: esas piezas quedan sin id hasta el primer
     *  guardado. */
    ids: readonly string[] = [],
  ): void {
    this._entradas = polygons.map((polygon, i) => ({
      polygon,
      modificada,
      id: ids[i] || null,
      feature: null,
      bbox: null,
    }));
  }

  /** Adopta los ids que devolvió el guardado, en el orden en que se mandaron
   *  las piezas.
   *
   *  **Se ignora si la cantidad no coincide.** Entre que el guardado salió y
   *  que volvió, el usuario pudo dibujar o borrar una pieza: ahí las posiciones
   *  ya no significan lo mismo y asignar por índice le pondría a una pieza el
   *  id de otra. Los ids viejos siguen valiendo —el servidor los conserva— y el
   *  guardado siguiente, que sale con el arreglo nuevo, vuelve a alinear. */
  adoptarIds(ids: readonly string[]): void {
    if (ids.length !== this._entradas.length) return;
    this._entradas.forEach((entrada, i) => {
      entrada.id = ids[i] || entrada.id;
    });
  }

  /** Los ids en orden, con cadena vacía donde la pieza todavía no tiene uno —
   *  que es lo que el request espera para decir "pieza nueva". */
  ids(): string[] {
    return this._entradas.map((entrada) => entrada.id ?? "");
  }

  /** Suma una pieza recién dibujada. Devuelve su `indice` (1-based). */
  agregar(polygon: Polygon): number {
    // Sin id: la pieza todavía no está en la base. Se lo pone `adoptarIds`
    // cuando el guardado vuelve.
    this._entradas.push({
      polygon,
      modificada: true,
      id: null,
      feature: null,
      bbox: null,
    });
    return this._entradas.length;
  }

  /** Reescribe la geometría de una pieza (la editó el usuario con los
   *  vértices). Queda marcada como modificada: ya no es la que se restauró.
   *
   *  @param modificada sólo se pasa para DESHACER una edición que el servidor
   *   rechazó (ver `AoiDrawToolbar.revertirEdicionRechazada`): ahí hay que
   *   devolver la pieza a su estado anterior, y eso incluye la marca. Si la
   *   pieza venía sin modificar, dejarla marcada haría que el `ConfirmarPiezas`
   *   la tome como cambiada y borre los lotes en los que ya estaba
   *   subdividida, por una edición que nunca se aplicó. */
  escribir(indice: number, polygon: Polygon, modificada = true): void {
    const entrada = this._entradas[indice - 1];
    if (!entrada) return;
    entrada.polygon = polygon;
    entrada.modificada = modificada;
    entrada.feature = null;
    entrada.bbox = null;
  }

  /** Quita una pieza. **Renumera** las que estaban después: el `indice` es la
   *  posición, no un identificador. Es correcto porque el guardado siguiente
   *  manda el arreglo completo y la base vuelve a numerar igual. */
  quitar(indice: number): void {
    if (indice < 1 || indice > this._entradas.length) return;
    this._entradas.splice(indice - 1, 1);
  }

  vaciar(): void {
    this._entradas = [];
  }

  get total(): number {
    return this._entradas.length;
  }

  /** Las piezas con su índice y su marca de modificada — lo que viaja en el
   *  `ConfirmarPiezas`. */
  piezas(): AoiPieza[] {
    return this._entradas.map((entrada, i) => ({
      indice: i + 1,
      polygon: entrada.polygon,
      modificada: entrada.modificada,
      id: entrada.id,
    }));
  }

  /** Solo las geometrías, en orden. Es el arreglo que se guarda. */
  geometrias(): Polygon[] {
    return this._entradas.map((entrada) => entrada.polygon);
  }

  pieza(indice: number): AoiPieza | null {
    const entrada = this._entradas[indice - 1];
    if (!entrada) return null;
    return {
      indice,
      polygon: entrada.polygon,
      modificada: entrada.modificada,
      id: entrada.id,
    };
  }

  /** `[minLng, minLat, maxLng, maxLat]` de una pieza, para acotar el refresco
   *  de tiles del WMS al área que cambió. */
  bbox(indice: number): [number, number, number, number] | null {
    const entrada = this._entradas[indice - 1];
    return entrada ? this._bboxDe(entrada) : null;
  }

  /** La pieza que está bajo un punto, o `null`. **Hit-testing del click**: el
   *  borde cuenta como adentro (sin `ignoreBoundary`), porque un click justo
   *  sobre el contorno tiene que seleccionar la pieza. */
  enElPunto(lng: number, lat: number): number | null {
    const punto = turf.point([lng, lat]);
    for (const { indice, feature, bbox } of this.recorrer()) {
      if (
        lng < bbox[0] || lng > bbox[2] ||
        lat < bbox[1] || lat > bbox[3]
      ) continue;
      try {
        if (turf.booleanPointInPolygon(punto, feature)) return indice;
      } catch {
        // Geometría inválida: no puede capturar el click.
      }
    }
    return null;
  }

  /** La pieza que CONTIENE a un punto, o `null` — con `ignoreBoundary: true`,
   *  así apoyarse en un borde ajeno sigue siendo válido.
   *
   *  Es la regla del dibujo, no la del click: dos piezas solapadas rompen la
   *  unión topológica del AOI, así que no se admite un vértice dentro de otra
   *  pieza. `excluir` deja fuera a la que se está editando, cuyos vértices
   *  están por definición sobre su propio borde. */
  contieneElPunto(lng: number, lat: number, excluir?: number): number | null {
    const punto = turf.point([lng, lat]);
    for (const { indice, feature, bbox } of this.recorrer(excluir)) {
      if (
        lng < bbox[0] || lng > bbox[2] ||
        lat < bbox[1] || lat > bbox[3]
      ) continue;
      try {
        if (turf.booleanPointInPolygon(punto, feature, { ignoreBoundary: true })) {
          return indice;
        }
      } catch {
        // Geometría inválida: no puede bloquear el dibujo.
      }
    }
    return null;
  }

  /** La pieza con la que un anillo SOLAPA EN ÁREA, o `null`.
   *
   *  Se mide el área de la intersección y se compara contra `areaMinimaM2`:
   *  dos polígonos que solo comparten un borde dan una intersección de área
   *  despreciable (ruido de coma flotante en los vértices compartidos), y
   *  apoyarse en un límite es válido. */
  solapa(
    anillo: readonly Position[],
    areaMinimaM2: number,
    excluir?: number,
  ): number | null {
    let nuevo;
    try {
      nuevo = turf.polygon([[...anillo]]);
    } catch {
      // Anillo degenerado: ya lo rechaza el chequeo de auto-intersección.
      return null;
    }

    for (const { indice, feature } of this.recorrer(excluir)) {
      try {
        const corte = turf.intersect(turf.featureCollection([nuevo, feature]));
        if (corte && turf.area(corte) > areaMinimaM2) return indice;
      } catch {
        // Geometría inválida: no puede bloquear el dibujo.
      }
    }
    return null;
  }

  /** Recorre las piezas con su geometría de turf y su caja ya calculadas (y
   *  cacheadas). Lo usan los chequeos geométricos del motor, que necesitan la
   *  geometría cruda — la aritmética de tramos vive allá, no acá. */
  *recorrer(excluir?: number): Generator<{
    indice: number;
    feature: Feature<Polygon>;
    bbox: [number, number, number, number];
  }> {
    for (let i = 0; i < this._entradas.length; i++) {
      const indice = i + 1;
      if (indice === excluir) continue;
      const entrada = this._entradas[i];
      const feature = this._featureDe(entrada);
      if (!feature) continue;
      yield { indice, feature, bbox: this._bboxDe(entrada) ?? [0, 0, 0, 0] };
    }
  }

  private _entradas: Entrada[] = [];

  private _featureDe(entrada: Entrada): Feature<Polygon> | null {
    if (entrada.feature) return entrada.feature;
    try {
      entrada.feature = turf.polygon(
        entrada.polygon.coordinates as Position[][],
      ) as Feature<Polygon>;
    } catch {
      entrada.feature = null;
    }
    return entrada.feature;
  }

  private _bboxDe(entrada: Entrada): [number, number, number, number] | null {
    if (entrada.bbox) return entrada.bbox;
    const feature = this._featureDe(entrada);
    if (!feature) return null;
    try {
      const b = turf.bbox(feature);
      entrada.bbox = [b[0], b[1], b[2], b[3]];
    } catch {
      entrada.bbox = null;
    }
    return entrada.bbox;
  }
}
